import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Wifi, Tv, Monitor, Globe, ArrowRight, ExternalLink,
  Signal, Users, Zap, ChevronRight
} from "lucide-react";

const brands = [
  {
    id: "ant-broadband",
    name: "ANT Broadband",
    tagline: "Ultra Fast Internet & Entertainment",
    url: "https://www.antbroadband.com",
    color: "#0A192F",
    accent: "#EA580C",
    icon: Wifi,
    logo: "/images/ant-broadband-logo.png",
    description:
      "ANT Broadband is our flagship internet service brand delivering high-speed fiber broadband to homes and businesses across India. With packages ranging from 50 Mbps to 1 Gbps, ANT Broadband combines reliable connectivity with competitive pricing.",
    features: [
      "Fiber broadband speeds up to 1 Gbps",
      "DDOS and DPI protection included",
      "Free WiFi router with select plans",
      "Unlimited data on premium packages",
      "24/7 customer support",
      "Coverage across major Indian cities",
    ],
    stats: [
      { label: "Max Speed", value: "1 Gbps" },
      { label: "Subscribers", value: "800K+" },
      { label: "Cities", value: "50+" },
    ],
  },
  {
    id: "a-digital",
    name: "A Digital",
    tagline: "Digital Content & IPTV Platform",
    url: "https://www.adgital.tv",
    color: "#EA580C",
    accent: "#0A192F",
    icon: Monitor,
    logo: "/images/a-digital-logo.png",
    description:
      "A Digital is our digital content and IPTV distribution platform, powering entertainment for over 100,000 households. As a TRAI-approved MSO, A Digital delivers 700+ channels including HD, regional, and international content.",
    features: [
      "700+ live TV channels",
      "HD and 4K content available",
      "Regional language packs",
      "Popular OTT integration (Jio Cinema, SonyLiv, Zee5)",
      "Electronic Program Guide (EPG)",
      "Time-shift and catch-up TV",
    ],
    stats: [
      { label: "Channels", value: "700+" },
      { label: "Subscribers", value: "100K+" },
      { label: "Languages", value: "15+" },
    ],
  },
  {
    id: "ant-tv",
    name: "ANT TV",
    tagline: "Premium Television Services",
    url: "#",
    color: "#0A192F",
    accent: "#F97316",
    icon: Tv,
    logo: "/images/ant-tv-logo.png",
    description:
      "ANT TV is our premium television entertainment brand, combining traditional broadcast with modern streaming capabilities. It offers curated content packages, live sports, and on-demand entertainment for the discerning viewer.",
    features: [
      "Curated entertainment packages",
      "Live sports and events streaming",
      "On-demand movie library",
      "Multi-device viewing support",
      "Parental control features",
      "Voice-enabled remote control",
    ],
    stats: [
      { label: "Content", value: "10K+ hrs" },
      { label: "Sports", value: "Live" },
      { label: "Devices", value: "Multi" },
    ],
  },
];

export default function Brands() {
  return (
    <div data-testid="brands-page">
      {/* Hero */}
      <section data-testid="brands-hero" className="relative py-24 md:py-32 bg-[#0A192F] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-[#EA580C] rounded-full blur-[100px]" />
          <div className="absolute top-20 right-40 w-56 h-56 bg-[#F97316] rounded-full blur-[80px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Our Brands
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-['Outfit'] leading-[1.1] mb-6">
            Three Brands,
            <br />
            <span className="text-[#F97316]">One Vision</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Each brand under Assistive Netspeed Technologies is purpose-built to serve a specific
            facet of India's digital needs &mdash; from broadband to content to entertainment.
          </p>
        </div>
      </section>

      {/* Brands Detail */}
      <section data-testid="brands-detail" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
          {brands.map((brand, idx) => {
            const Icon = brand.icon;
            const isReversed = idx % 2 !== 0;
            return (
              <div
                key={brand.id}
                data-testid={`brand-detail-${brand.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${
                  isReversed ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={isReversed ? "lg:order-2" : ""}>
                  <div
                    className="p-10 h-full min-h-[400px] flex flex-col justify-center"
                    style={{ background: brand.color }}
                  >
                    <div className="w-20 h-20 rounded-sm flex items-center justify-center mb-6 bg-white p-2">
                      <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-2">
                      {brand.name}
                    </h2>
                    <p className="text-base text-white/70 mb-6">{brand.tagline}</p>
                    <div className="grid grid-cols-3 gap-4">
                      {brand.stats.map((stat) => (
                        <div key={stat.label}>
                          <div className="text-2xl font-bold text-white font-['Outfit']">
                            {stat.value}
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-white/50">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className={isReversed ? "lg:order-1" : ""}>
                  <Badge className="bg-[#EA580C]/10 text-[#EA580C] text-[10px] uppercase tracking-wider mb-4 hover:bg-[#EA580C]/10">
                    Brand
                  </Badge>
                  <h3 className="text-2xl font-semibold text-[#0A192F] font-['Outfit'] mb-4">
                    {brand.name}
                  </h3>
                  <p className="text-base leading-relaxed text-[#334155] mb-6">
                    {brand.description}
                  </p>
                  <h4 className="text-sm font-bold uppercase tracking-[0.15em] text-[#0A192F] mb-4">
                    Key Features
                  </h4>
                  <ul className="space-y-3 mb-8">
                    {brand.features.map((f) => (
                      <li key={f} className="flex gap-3 items-start">
                        <Zap className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#334155]">{f}</span>
                      </li>
                    ))}
                  </ul>
                  {brand.url !== "#" && (
                    <a href={brand.url} target="_blank" rel="noopener noreferrer">
                      <Button
                        data-testid={`visit-${brand.id}-btn`}
                        variant="outline"
                        className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white rounded-sm"
                      >
                        Visit {brand.name} <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Brand Synergy */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            The Ecosystem
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-6 max-w-2xl mx-auto">
            An Integrated Digital Ecosystem
          </h2>
          <p className="text-base text-[#64748B] max-w-xl mx-auto mb-12">
            Our three brands work in synergy &mdash; ANT Broadband provides the pipe,
            A Digital delivers the content, and ANT TV brings the entertainment experience home.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <div className="w-40 h-40 bg-[#0A192F] flex flex-col items-center justify-center p-4">
              <img src="/images/ant-broadband-logo.png" alt="ANT Broadband" className="w-16 h-16 object-contain mb-2 bg-white rounded-sm p-1" />
              <span className="text-white font-['Outfit'] font-semibold text-sm">ANT Broadband</span>
              <span className="text-[10px] text-white/50 mt-1">The Pipe</span>
            </div>
            <ChevronRight className="w-6 h-6 text-[#EA580C] rotate-90 md:rotate-0" />
            <div className="w-40 h-40 bg-[#EA580C] flex flex-col items-center justify-center p-4">
              <img src="/images/a-digital-logo.png" alt="A Digital" className="w-16 h-16 object-contain mb-2 bg-white rounded-sm p-1" />
              <span className="text-white font-['Outfit'] font-semibold text-sm">A Digital</span>
              <span className="text-[10px] text-white/70 mt-1">The Content</span>
            </div>
            <ChevronRight className="w-6 h-6 text-[#EA580C] rotate-90 md:rotate-0" />
            <div className="w-40 h-40 bg-[#0A192F] flex flex-col items-center justify-center p-4">
              <img src="/images/ant-tv-logo.png" alt="ANT TV" className="w-16 h-16 object-contain mb-2 bg-white rounded-sm p-1" />
              <span className="text-white font-['Outfit'] font-semibold text-sm">ANT TV</span>
              <span className="text-[10px] text-white/50 mt-1">The Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white font-['Outfit'] mb-4">
            Partner with Our Brands
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Interested in channel partnerships, reseller programs, or white-label solutions? Let's talk.
          </p>
          <Link to="/contact">
            <Button
              data-testid="brands-cta-btn"
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-8 py-6 text-sm font-semibold"
            >
              Get in Touch <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
